import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import WarningOutlined from '@mui/icons-material/WarningOutlined'
import { LoadingButton } from '@mui/lab'
import { Stack, Button, Typography } from '@mui/material'
import { ProjectGridField } from '@/types/fields'
import { ProjectDocumentRule, ProjectGridFieldRule } from '@/types/rules'
import { getDefaultValue, RuleProperty, updateParams } from '@/utils/rules'
import { PixyDocsForm } from '@/components/forms'
import CommonRuleConfiguration from './CommonRuleConfiguration'
import RuleCodeEditor from './RuleCodeEditor'

export type FormValues = {
  workflow_states_ids: string[]
  run_in_all_workflow_states: boolean
  dependent_fields: Pick<ProjectGridField, 'id'>[]
  is_superuser_rule: boolean
} & ProjectGridFieldRule['params'] // params type is the same for both document and rule types

export type RuleSettingsProps<
  T extends ProjectGridFieldRule | ProjectDocumentRule,
> = {
  isLoading: boolean
  fields: ProjectGridField[]
  rule: T
  onSubmit: (values: FormValues) => Promise<T | void>
  fieldId?: string // Only used for field rules
}

// To make TS happy
export function isProjectGridFieldRule(
  rule: ProjectGridFieldRule | ProjectDocumentRule,
): rule is ProjectGridFieldRule {
  return 'rule' in rule
}

function getPropertyFormValues({
  rule,
  fields,
}: {
  rule: ProjectGridFieldRule | ProjectDocumentRule
  fields: ProjectGridField[]
}) {
  const { params, workflow_states_ids = [] } = rule
  const isFieldRule = isProjectGridFieldRule(rule)

  const run_in_all_workflow_states = rule.run_in_all_workflow_states ?? false

  const type = isFieldRule ? rule.rule : rule.type
  const { params_schema } = type

  // Sort the properties by sort_order
  const properties = Object.entries(
    (params_schema.properties as RuleProperty) || [],
  ).sort((p1, p2) => p1[1].sort_order - p2[1].sort_order)

  // We use 'params' for the default values, but if any are missing we need to give
  // them a default of an empty string to prevent uncontrolled-to-controlled errors.
  const defaultValues = properties.reduce((acc, [propertyId, property]) => {
    if (property.hidden) return acc
    return {
      ...acc,
      [propertyId]: getDefaultValue(property) ?? '',
    }
  }, {})

  // In the case a schema has had a field removed, but it was previously put on the rule,
  // we need to remove it. If we include it in the defaultValues, then it gets sent to
  // the backend in the update call.
  const scrubbedParams = Object.fromEntries(
    Object.entries(params).filter(([paramId]) => {
      const propertyObj = params_schema.properties?.[paramId]
      return propertyObj && !propertyObj.hidden
    }),
  )

  return {
    defaultValues: {
      ...defaultValues,
      ...scrubbedParams,
      workflow_states_ids,
      run_in_all_workflow_states,
      dependent_fields: fields.filter(({ id }) =>
        rule.dependent_fields_ids.includes(id),
      ),
      is_superuser_rule: rule.is_superuser_rule,
    } as FormValues,
    properties,
  }
}

function updateParamsForRule(
  newParams: ProjectGridFieldRule['params'],
  rule: ProjectGridFieldRule | ProjectDocumentRule,
) {
  const isFieldRule = isProjectGridFieldRule(rule)

  const type = isFieldRule ? rule.rule : rule.type
  const { params_schema } = type

  return updateParams(newParams, params_schema.properties)
}

export default function RuleSettings<
  T extends ProjectGridFieldRule | ProjectDocumentRule,
>({ isLoading, rule, onSubmit, fields, fieldId }: RuleSettingsProps<T>) {
  const { defaultValues, properties } = useMemo(
    () => getPropertyFormValues({ rule, fields }),
    [rule, fields],
  )

  const methods = useForm<FormValues>({
    defaultValues,
  })

  const {
    formState: { isDirty, isValid, dirtyFields },
    watch,
  } = methods

  function handleSubmit(values: FormValues) {
    const {
      workflow_states_ids,
      run_in_all_workflow_states,
      dependent_fields,
      is_superuser_rule,
      ...params
    } = values
    const updatedParams = updateParamsForRule(params, rule)
    onSubmit({
      workflow_states_ids,
      run_in_all_workflow_states,
      dependent_fields,
      is_superuser_rule,
      ...updatedParams,
    })
      .then(() => {
        // Clear dirty state, but keep current values
        methods.reset(methods.getValues(), { keepValues: true })
      })
      .catch(() => {})
  }

  const isFieldRule = isProjectGridFieldRule(rule)
  const pythonScript = watch('script')
  const functionName = watch('function_name')

  const type = isFieldRule ? rule.rule : rule.type
  const maxHeight = `calc(100vh - ${isFieldRule ? 340 : 300}px)`

  return (
    <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
      <Stack direction="row" spacing={1.5} sx={{ pl: 2 }}>
        <Typography variant="h6">{type.name}</Typography>
        {(type.code === 'run_py' || type.code === 'doc_run_py') && (
          <RuleCodeEditor
            isFieldRule={isFieldRule}
            isOnlyEditableBySuperUser={rule.is_superuser_rule}
            functionName={functionName}
            script={pythonScript}
            onSave={(value) => {
              methods.setValue('script', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
            color={dirtyFields.script ? 'secondary' : 'primary'}
          />
        )}
      </Stack>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Stack spacing={2} sx={{ pl: 2, maxHeight, overflow: 'auto' }}>
          <CommonRuleConfiguration
            propertiesToRender={properties}
            ruleType={type}
            fieldId={fieldId}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="end"
          alignItems="center"
        >
          {isDirty && (
            <Stack direction="row" alignItems="center" flexGrow={1} spacing={1}>
              <WarningOutlined fontSize="small" color="secondary" />
              <Typography color="secondary" variant="body2">
                <i>Unsaved Changes</i>
              </Typography>
            </Stack>
          )}
          <Button
            variant="text"
            disabled={!isDirty}
            onClick={() => methods.reset(defaultValues)}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={isLoading}
            disabled={!isDirty || !isValid}
            variant="contained"
            type="submit"
          >
            Save
          </LoadingButton>
        </Stack>
      </Stack>
    </PixyDocsForm>
  )
}
